import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useParams } from 'react-router-dom';
import { throttle } from 'throttle-debounce';

import { paymentViewSelector, paymentUpdateViewAction, loadingSelector, backSearchUrlSelector } from '@/domain/env';
import { paymentPaymentIdSelector } from '@/domain/payments';

import Box from '@mui/material/Box';
import LayoutPayPayment from '@/components/mui/Layouts/PayPayment';
import PayEventsTrail from '@/pages/dokkaPay/pages/company/components/PayEventsTrail';
import PaymentWorkspaceResizer from '@/pages/dokkaPay/pages/payment/components/PaymentWorkspaceResizer';
import PaymentTabs from '@/pages/dokkaPay/pages/payment/components/PaymentTabs';
import CircularProgressWithBackdrop from '@/components/mui/CircularProgressWithBackdrop';
import PaymentComponent from '@/pages/dokkaPay/pages/payment/components/PaymentComponent';
import PaymentFooter from '@/pages/dokkaPay/pages/payment/components/PaymentFooter';
import { PaymentPreviewPortalElement, PaymentWidgetPanel } from '@/pages/dokkaPay/pages/payment/StyledComponents';

import { queryToSearch } from '@/domain/router/helpers';
import ROUTES_PATH from '@/router/routesPathConfig';

import { PaymentContext } from '@/pages/dokkaPay/pages/payment/PaymentContext';

import { useTheme } from '@mui/material';

const MIN_WIDTH = 500;
// 8px - width separator, 34px - left bar width
// (8 + 34) / 2 = 21;
const OFFSET = 21;
const LOCAL_STORAGE_KEYS = {
  documentsWidth: 'payPaymentsWidth',
  infoBarWidth: 'payInfoBarWidth',
};

const PayPaymentPage: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  const { direction } = useTheme();
  const isRtl = direction === 'rtl';

  const paymentsWidth = useSelector((state) => parseInt(paymentViewSelector(state).get('payPaymentsWidth'), 10));
  const infoBarWidth = useSelector((state) => parseInt(paymentViewSelector(state).get('payInfoBarWidth'), 10));
  const loading = useSelector(loadingSelector);
  const backSearchUrl = useSelector(backSearchUrlSelector);
  const paymentId = useSelector(paymentPaymentIdSelector);

  const backLink = useMemo(() => {
    if (backSearchUrl) {
      return {
        pathname: generatePath(ROUTES_PATH.PAY_COMPANY.absolute, backSearchUrl.routeParams),
        search: queryToSearch(backSearchUrl.query),
      };
    }
    return generatePath(ROUTES_PATH.PAY_COMPANY.absolute, { companyId: params.companyId });
  }, [backSearchUrl, params.companyId]);

  const paymentContextValue = useMemo(
    () => ({
      backLink,
    }),
    [backLink],
  );

  const [leftWidth, setLeftWidth] = useState(0);
  const [rightWidth, setRightWidth] = useState(0);
  const [scale, setScale] = useState(1);
  const [paymentWidthBeforeResize, setPaymentWidthBeforeResize] = useState(0);

  const onScale = useCallback((mx: number) => {
    const newScale = Math.max(Math.min(4, mx), 1);
    setScale(newScale);
  }, []);

  const onZoom = useCallback(
    (direction: number) => {
      const factor = 0.2;

      if (direction > 0) {
        onScale(scale + factor);
      } else {
        onScale(scale - factor);
      }
    },
    [scale, onScale],
  );

  const onReset = useCallback(() => {
    setScale(1);
  }, []);

  const getIsDisabledZoomButton = useCallback(
    (direction: number) => {
      switch (direction) {
        case 1:
          return scale >= 4;
        case 0:
          return scale === 1;
        case -1:
          return scale <= 1;
        default:
          return true;
      }
    },
    [scale],
  );

  // On mount: calculate proportional or default
  useLayoutEffect(() => {
    const total = containerRef.current?.offsetWidth || window.innerWidth;

    if (!paymentsWidth || !infoBarWidth) {
      // If no saved sizes, divide equally with min width constraint
      const half = total / 2 - OFFSET;
      setLeftWidth(half);
      setRightWidth(half);
    } else {
      const scale = total / (paymentsWidth + infoBarWidth);
      setLeftWidth(paymentsWidth * scale - OFFSET);
      setRightWidth(infoBarWidth * scale - OFFSET);
    }
  }, [paymentsWidth, infoBarWidth]);

  // Resize listener to scale proportionally
  useEffect(() => {
    const onResize = () => {
      const total = containerRef.current?.offsetWidth || window.innerWidth;
      const full = paymentsWidth + infoBarWidth;
      if (!full) return;

      const scale = total / full;
      const newLeft = Math.max(Math.round(paymentsWidth * scale)) - OFFSET;
      const newRight = Math.max(Math.round(infoBarWidth * scale)) - OFFSET;

      setLeftWidth(newLeft);
      setRightWidth(newRight);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [paymentsWidth, infoBarWidth]);

  const saveSizes = useCallback(
    (doc: number, info: number) => {
      localStorage.setItem(LOCAL_STORAGE_KEYS.documentsWidth, String(doc));
      localStorage.setItem(LOCAL_STORAGE_KEYS.infoBarWidth, String(info));
      dispatch(paymentUpdateViewAction({ payPaymentsWidth: doc, payInfoBarWidth: info }));
    },
    [dispatch],
  );

  const onResize = useCallback(
    throttle(150, false, (event: MouseEvent) => {
      const total = containerRef.current?.offsetWidth || window.innerWidth;
      const newLeft =
        event.clientX + MIN_WIDTH < total ? Math.max(event.clientX, MIN_WIDTH) : Math.max(total - MIN_WIDTH, MIN_WIDTH);
      const newRight = Math.max(total - newLeft, MIN_WIDTH);

      saveSizes(newLeft, newRight);
    }) as (event: MouseEvent) => void,
    [saveSizes],
  );

  const paymentWidth = useMemo(() => (isRtl ? rightWidth : leftWidth), [isRtl, rightWidth, leftWidth]);
  const currentSidebar = useMemo(() => (isRtl ? 'right' : 'left'), [isRtl]);

  const onResizeStart = useCallback(() => {
    setPaymentWidthBeforeResize(paymentWidth);
    if (document.body) document.body.classList.add('UserSelect-none');
  }, [paymentWidth]);

  const onResizeEnd = useCallback(() => {
    setPaymentWidthBeforeResize(0);
    if (document.body) document.body.classList.remove('UserSelect-none');
  }, []);

  return (
    <PaymentContext.Provider value={paymentContextValue}>
      <LayoutPayPayment backTo={backLink}>
        {loading && <CircularProgressWithBackdrop BackdropProps={{ sx: { position: 'absolute' } }} />}
        {!paymentId ? null : (
          <>
            <Box
              ref={containerRef}
              style={{ display: 'flex', flexDirection: 'row', flex: 1, width: '100%', overflow: 'hidden' }}
            >
              <PaymentWidgetPanel hide={currentSidebar === 'right'}>
                <PaymentPreviewPortalElement id="doc-preview" sx={{ left: 0 }}>
                  {children}
                </PaymentPreviewPortalElement>
              </PaymentWidgetPanel>
              <Box
                style={{
                  width: `calc(${paymentWidth}px - ${OFFSET}px`,
                  boxSizing: 'border-box',
                }}
              >
                <PaymentComponent
                  onScale={onScale}
                  scale={scale}
                  width={paymentWidth}
                  startResizeWidth={paymentWidthBeforeResize}
                  currentSidebar={currentSidebar}
                  onZoom={onZoom as unknown as (type: string) => void}
                  onReset={onReset}
                  getIsDisabledZoomButton={getIsDisabledZoomButton as unknown as (type: string) => boolean}
                />
              </Box>

              <PaymentWorkspaceResizer
                isVertical
                onResize={onResize}
                onResizeStart={onResizeStart}
                onResizeEnd={onResizeEnd}
              />

              <Box
                style={{
                  width: `calc(${isRtl ? leftWidth : rightWidth}px + ${OFFSET}px`,
                  boxSizing: 'border-box',
                }}
              >
                <PaymentTabs />
              </Box>
              <PaymentWidgetPanel hide={currentSidebar === 'left'}>
                <PaymentPreviewPortalElement id="doc-preview-rtl" sx={{ right: 0 }}>
                  {children}
                </PaymentPreviewPortalElement>
              </PaymentWidgetPanel>
            </Box>
            <PaymentFooter />
          </>
        )}
        <PayEventsTrail paymentId={params.paymentId} />
      </LayoutPayPayment>
    </PaymentContext.Provider>
  );
};

export default PayPaymentPage;
